import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listDeletedProjects, purgeProject, restoreProject, type DeletedProject } from "../../api/projectLifecycleApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { useProjects } from "../../projects/projectContext";
import { personName } from "../../projects/settings/settingsTexts";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import Chip from "../../ui/feedback/Chip";
import EmptyState from "../../ui/feedback/EmptyState";
import LoadStatus from "../../ui/feedback/LoadStatus";
import Section from "../../ui/forms/Section";
import ConfirmAction from "../../ui/overlays/ConfirmAction";

/**
 * `/administration/deleted-projects` (F121), as GitHub's deleted repositories: who deleted each
 * project and when; restoring it as it was; and, once its restore period is over, purging it
 * for good, confirmed by typing its name. A purged project stays listed, so it is known who
 * deleted and purged it.
 */
export default function DeletedProjectsPage() {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listDeletedProjects(signal), []);
  const { state, reload, refresh } = useLoad(load);
  return (
    <div className="max-w-4xl space-y-6">
      <Section title={t("administration.deletedProjects.title")} hint={t("administration.deletedProjects.hint")}>
        <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
        {state.status === "loaded" && (state.value.length === 0 ? (
          <EmptyState>{t("administration.deletedProjects.none")}</EmptyState>
        ) : (
          <ul className="app-divide-y text-sm">
            {[...state.value].reverse().map((project) => <DeletedProjectRow key={project.projectId} project={project} onChanged={refresh} />)}
          </ul>
        ))}
      </Section>
    </div>
  );
}

function DeletedProjectRow({ project, onChanged }: { project: DeletedProject; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const { reloadProjects } = useProjects();
  const { state, submit } = useSubmission();
  const [purging, setPurging] = useState(false);
  // When the row was shown; the server refuses a purge that is early by then, saying so.
  const [shownAt] = useState(() => Date.now());
  const purgeDue = Date.parse(project.purgeableFrom) <= shownAt;
  const when = (time: string) => formatDateTime(time, i18n.language);
  let purgeUnavailable: string | undefined;
  if (!project.purgeAvailable) {
    purgeUnavailable = t("administration.deletedProjects.purgeNotBuilt");
  } else if (!purgeDue && project.state === "deleted") {
    purgeUnavailable = t("administration.deletedProjects.purgeNotYet", { time: when(project.purgeableFrom) });
  }

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <p className="truncate font-semibold">{project.name}</p>
          <p className="app-text-muted text-xs">
            {t("administration.deletedProjects.deleted", { time: when(project.deletedAt), name: personName(t, project.deletedBy) })}
          </p>
          {project.state === "purged" && project.purgedAt !== null && project.purgedBy !== null ? (
            <p className="app-text-muted text-xs">
              {t("administration.deletedProjects.purged", { time: when(project.purgedAt), name: personName(t, project.purgedBy) })}
            </p>
          ) : null}
          {project.state === "deleted" ? (
            <p className="app-text-muted text-xs">{t("administration.deletedProjects.purgeableFrom", { time: when(project.purgeableFrom) })}</p>
          ) : null}
          <div className="mt-1 flex flex-wrap gap-2">
            <Chip>{t(`administration.deletedProjects.states.${project.state}`)}</Chip>
          </div>
        </div>
        {project.state === "purged" ? null : (
          <div className="flex shrink-0 gap-2">
            {project.state === "deleted" ? (
              <AppButton size="sm" variant="primary" disabled={state.status === "submitting"}
                onClick={() => {
                  submit(async () => {
                    await restoreProject(project.projectId);
                    reloadProjects();
                    onChanged();
                  });
                }}>
                {t("administration.deletedProjects.restore")}
              </AppButton>
            ) : null}
            <AppButton size="sm" variant="dangerSolid" disabled={state.status === "submitting"} unavailable={purgeUnavailable}
              onClick={() => { setPurging(true); }}>
              {t("administration.deletedProjects.purge")}
            </AppButton>
          </div>
        )}
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      {purging ? (
        <ConfirmAction
          title={t("administration.deletedProjects.purgeTitle", { project: project.name })}
          confirmLabel={t("administration.deletedProjects.purge")}
          tone="dangerSolid"
          typedConfirmation={project.name}
          onConfirm={async () => {
            await purgeProject(project.projectId, project.name);
            onChanged();
          }}
          onClose={() => { setPurging(false); }}
        >
          <p>{t("administration.deletedProjects.purgeMessage")}</p>
        </ConfirmAction>
      ) : null}
    </li>
  );
}
